import { ParamListBase, RouteProp } from '@react-navigation/native';
import { DETAILS_SCREEN_TAB_NAME, TABLE_NAMES } from '@v2/pages/common/constants/base.constants';
import { LevelSubScreen, ModalType, Perspective } from '@v2/pages/common/enums';
import { font } from '@styles/fonts';
import svgRegister from '@styles/icons/svg/svgRegister';
import { IconConfig, ScaleFunction } from '@components/operatorPanel/interfaces';
import { StyleProp, ViewStyle } from 'react-native';
import { NCFileSmartLink } from '@interfaces/search';
import { MachineStatus } from '@interfaces/machines';
import { KEYS_TAGS } from '@components/imageHandling/common';

export interface Position {
  x: number;
  y: number;
}

export interface Measurements {
  width: number;
  height: number;
}

export interface ConnectorDetails {
  connectorId: string;
  dnsSuffix: string;
  ipAddressLocal: string;
  ipAddressV4: string;
  localHost: string;
  macAddress: string;
  status: {
    lastAliveEventReceivedOn: string;
    status: string;
    workerType: string;
  }[];
  version: string;
}

export interface EditingPartData {
  partName?: string;
  description?: string;
  drawing?: number;
  revision?: number;
  position?: string;
}

export interface InfoScreen extends SimpleOnPress {
  name: (typeof DETAILS_SCREEN_TAB_NAME)[keyof typeof DETAILS_SCREEN_TAB_NAME];
  key: string;
  value: string | undefined;
  shouldDisplayModalTriggerButton: boolean;
  modalType: ModalType;
  preselected: boolean;
  plural: boolean;
  children: LevelSubScreen[];
  perspective: Perspective;
  route: RouteProp<ParamListBase>;
  mainScreen: string;
}

export type SearchItemType = 'Part' | 'Operation' | 'NcFile' | 'ProductionOrder' | 'Tool' | 'Machine' | 'UserAccount';

export interface SignalREventResponse {
  errorMessage: string | null;
  isError: boolean;
}

export interface ActionMenuConfigItem extends SimpleOnPress {
  lable: {
    color: string;
    name: string;
    font: (typeof font)[keyof typeof font];
  };
  icon: Omit<IconConfig, 'icon'> & { name: keyof typeof svgRegister };
  hide?: boolean;
  iconsGap?: number;
  icons?: Omit<IconConfig, 'icon'> & { name: keyof typeof svgRegister }[];
  subtitle?: boolean;
  style?: StyleProp<ViewStyle>;
}

export enum TimeCategory {
  ACTUAL = 'Actual',
  SCHEDULED = 'Scheduled',
  IDEAL = 'Ideal',
  PAID = 'Paid',
}

export interface MachineCountSource {
  isUsed: boolean;
  name: string;
  quantity: number;
  quantityRaw: string;
  quantityType: string;
}

export interface MachineCountingDetails {
  countingMethod: string;
  machineCountSources: MachineCountSource[];
  quantityDelta: number;
}

export interface RawMachineEventDataNcFileDetails {
  $type: string;
  hierarchyLevel: number;
  ncFileName: string;
  sequenceNumber: number;
  transferState: { transferState: string; transferStateId: string };
}

export interface RawMachineEventData {
  $type: string;
  currentBlockNumberRaw: string | null;
  executionMode: string;
  executionModeRaw: string;
  machineCountingDetails: MachineCountingDetails;
  ncFiles: RawMachineEventDataNcFileDetails[];
  sequenceNumber: number;
  subStatus: string;
  subStatusRaw: string;
  doIgnoreMachineStatusVerification: boolean;
  machineId: number;
  sentFromBackend: boolean;
  sentFromCache: boolean;
  timestamp: string;
  tryCount: number;
}

export interface ContainerStyle {
  containerStyle?: StyleProp<ViewStyle>;
}

// Optional scale/font props so a component can render at an injected scale (e.g. scaleOP/fontOP in the Operator Panel).
export interface WithScaleFont {
  scale?: ScaleFunction;
  font?: typeof font;
}

export interface SimpleOnPress {
  onPress: () => void;
}

export interface OnAddedProductionInformationEventPayload {
  ncSmartLinks: NCFileSmartLink[];
  clampingDetails: MachineStatus['clampingDetails'];
  productionBatchId: number | null;
}

export interface TextConfig {
  color: string;
  fontType: keyof typeof font;
  text: string;
  textAlreadyTranslated?: boolean;
}

export type ScopeId = (typeof KEYS_TAGS)[keyof typeof KEYS_TAGS];

export interface AnimationConfig {
  duration: number;
  maxScale?: number;
  minScale?: number;
  startOpacity?: number;
}

export type TableName = (typeof TABLE_NAMES)[keyof typeof TABLE_NAMES];
